import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { formatCurrency } from '../../config/currency';
import { useCountryConfig } from '../../hooks/useCountryConfig';
import { eodApi, EodSummaryResponse, ManualEntry } from '../../api/eodApi';
import { financeApi, RevenueChannel, DailyRevenueEntry } from '../../api/financeApi';
import FinanceTabBar from '../../components/finance/FinanceTabBar';
import RevenueBreakdownForm from '../../components/finance/RevenueBreakdownForm';
import { PageHeroBand, LoadingSkeleton } from '../../components/ui';
import SettlementRecoveryDialog, {
  EntrySettledDetails,
} from '../../components/finance/SettlementRecoveryDialog';

/* ------------------------------------------------------------------ */
/*  Auto-populated field definitions                                   */
/* ------------------------------------------------------------------ */

interface AutoFieldDef {
  section: string;
  label: string;
  isCount?: boolean;
}

const AUTO_FIELDS: AutoFieldDef[] = [
  { section: 'REVENUE', label: 'Gross Revenue' },
  { section: 'REVENUE', label: 'Net Revenue' },
  { section: 'TAX', label: 'CGST Collected' },
  { section: 'OPERATIONS', label: 'Total Covers', isCount: true },
  { section: 'REVENUE', label: 'Total Discounts' },
];

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function EodSummary() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { theme } = useTheme();
  const { user } = useAuth();
  const { taxSublabels } = useCountryConfig();
  // Display-only: the backend match-key stays 'CGST Collected'; UI shows the country's tax label.
  const displayFieldLabel = (field: AutoFieldDef) =>
    field.section === 'TAX' ? `${taxSublabels[0] || 'CGST'} Collected` : field.label;
  const isFinanceOnlyRestricted = Boolean(user?.financeOnlySteward);

  const dateParam = searchParams.get('date');
  const isHistorical = !!dateParam;

  // Auto-populated values (read-only)
  const [autoValues, setAutoValues] = useState<Record<string, number | null>>({});
  const [summary, setSummary] = useState<EodSummaryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [settlementBlock, setSettlementBlock] = useState<EntrySettledDetails | null>(null);
  const [showDialog, setShowDialog] = useState(false);
  const isConfirmed = summary?.status === 'CONFIRMED';

  // Revenue channel state
  const [channels, setChannels] = useState<RevenueChannel[]>([]);
  const [channelValues, setChannelValues] = useState<Record<number, string>>({});

  // Manual entries state
  const [tipsTotal, setTipsTotal] = useState('');
  const [staffMealsCount, setStaffMealsCount] = useState('');
  const [staffMealsValue, setStaffMealsValue] = useState('');
  const [closingCash, setClosingCash] = useState('');
  const [closingNotes, setClosingNotes] = useState('');

  const todayStr = useRef(new Date().toISOString().slice(0, 10));
  const summaryDate = dateParam || todayStr.current;

  /* -- Load summary ------------------------------------------------ */

  const loadSummary = useCallback(async () => {
    setLoading(true);
    try {
      let data: EodSummaryResponse | null = null;

      // Try generate first (works for any date), fallback to getSummary if already confirmed (409)
      try {
        const res = await eodApi.generateSummary(summaryDate);
        data = res.data;
      } catch (err: unknown) {
        if ((err as { response?: { status?: number } })?.response?.status === 409) {
          const res = await eodApi.getSummary(summaryDate);
          data = res.data;
        }
      }

      if (data) {
        setSummary(data);
        // Extract auto-populated values
        const av: Record<string, number | null> = {};
        for (const field of AUTO_FIELDS) {
          const line = data.lines.find(
            (l) => l.section === field.section && l.label === field.label,
          );
          if (line) {
            // POS Summary section must reflect ONLY POS-derived auto values
            // (autoValue), never finalValue — otherwise it shows the
            // post-confirm rollup (POS + channel totals), which lies about
            // what the POS itself contributed.
            av[field.label] = line.autoValue ?? null;
          }
        }
        setAutoValues(av);

        // Manual entries for THIS date — set unconditionally, including when the
        // day has none.
        //
        // These used to be applied only when the incoming value was non-null, so
        // switching to a day with nothing recorded left the PREVIOUS day's tips,
        // staff meals, closing cash and notes sitting in the form under the new
        // date. A day could be confirmed carrying another day's figures.
        const manualOf = (label: string): string => {
          const line = data!.lines.find((l) => l.label === label);
          return line?.manualValue != null ? String(line.manualValue) : '';
        };
        setTipsTotal(manualOf('Tips Total'));
        setStaffMealsCount(manualOf('Staff Meals Count'));
        setStaffMealsValue(manualOf('Staff Meals Value'));
        setClosingCash(manualOf('Closing Cash'));
        setClosingNotes(data.closingNotes ?? '');
      }
    } catch {
      // Nothing for this date — clear the form rather than leaving the previous
      // day's figures under a new heading.
      setSummary(null);
      setAutoValues({});
      setTipsTotal('');
      setStaffMealsCount('');
      setStaffMealsValue('');
      setClosingCash('');
      setClosingNotes('');
    } finally {
      setLoading(false);
    }
  }, [dateParam, summaryDate]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  /* -- Load channels ----------------------------------------------- */

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        let res = await financeApi.getChannels();
        let ch = res.data;
        if (!ch || ch.length === 0) {
          await financeApi.seedDefaultChannels();
          res = await financeApi.getChannels();
          ch = res.data;
        }
        if (!cancelled) setChannels(ch);

        // Load saved channel values for the active day (today or selected
        // date). Previously gated on dateParam, which left "today" inputs
        // blank even after a confirmed day with saved channel amounts.
        try {
          const revRes = await financeApi.getDailyRevenue(summaryDate);
          const entries = revRes.data;
          if (!cancelled) {
            // Set even when the day has no entries, so switching dates clears
            // the boxes rather than carrying the last day's amounts across.
            const cv: Record<number, string> = {};
            if (Array.isArray(entries)) {
              entries.forEach((e) => {
                cv[e.channelId] = String(e.grossAmount);
              });
            }
            setChannelValues(cv);
          }
        } catch {
          // No saved revenue for this day — leave the boxes empty rather than
          // showing another day's.
          if (!cancelled) setChannelValues({});
        }
      } catch {
        // no channels available
      }
    })();
    return () => { cancelled = true; };
  }, [dateParam, summaryDate]);

  /* -- Input helpers ----------------------------------------------- */

  const sanitizeDecimal = (raw: string): string => {
    let clean = raw.replace(/[^0-9.]/g, '');
    const parts = clean.split('.');
    if (parts.length > 2) clean = parts[0] + '.' + parts.slice(1).join('');
    return clean;
  };

  const sanitizeInt = (raw: string): string => raw.replace(/[^0-9]/g, '');

  const handleChannelChange = (channelId: number, grossAmount: string) => {
    setChannelValues((prev) => ({ ...prev, [channelId]: grossAmount }));
  };

  /* -- Can submit? ------------------------------------------------- */

  const hasChannelValues = channels.some((ch) => channelValues[ch.id] && channelValues[ch.id] !== '');
  const hasManualValues = [tipsTotal, staffMealsCount, staffMealsValue, closingCash].some(
    (v) => v && v !== '' && v !== '0',
  );
  const hasNegativeChannels = channels.some((ch) => {
    const val = parseFloat(channelValues[ch.id] || '') || 0;
    return val < 0;
  });
  // A day with real POS takings is closable as-is — channel/manual entries are
  // only required when there is nothing at all to confirm. (The old gate keyed
  // off typed entries only, so a POS-only day could never be closed.)
  const hasPosRevenue = (summary?.grossRevenue ?? 0) > 0 || (summary?.covers ?? 0) > 0;
  const canSubmit = (hasPosRevenue || hasChannelValues || hasManualValues) && !hasNegativeChannels;

  /* -- Submit ------------------------------------------------------ */

  const handleSubmit = () => {
    setShowDialog(true);
  };

  const handleConfirmClose = async () => {
    setShowDialog(false);
    setSubmitting(true);
    try {
      const manualEntries: ManualEntry[] = [];

      if (tipsTotal) {
        manualEntries.push({ section: 'OPERATIONS', label: 'Tips Total', manualValue: parseFloat(tipsTotal) });
      }
      if (staffMealsCount) {
        manualEntries.push({
          section: 'OPERATIONS',
          label: 'Staff Meals Count',
          manualValue: parseInt(staffMealsCount, 10),
          itemCount: parseInt(staffMealsCount, 10),
        });
      }
      if (staffMealsValue) {
        manualEntries.push({ section: 'OPERATIONS', label: 'Staff Meals Value', manualValue: parseFloat(staffMealsValue) });
      }
      if (closingCash) {
        manualEntries.push({ section: 'OPERATIONS', label: 'Closing Cash', manualValue: parseFloat(closingCash) });
      }

      // Compute revenue channel totals up-front so they roll into Gross/Net Revenue.
      // EodHistory reads s.grossRevenue/netRevenue — without this, days with only
      // channel-entered revenue (no POS orders) would show £0 in history.
      // Note: commission is intentionally NOT subtracted here. EOD totals use gross
      // only; commissionPercent stays on the channel as informational metadata.
      let channelGross = 0;
      let channelNet = 0;
      channels.forEach((ch) => {
        const gross = parseFloat(channelValues[ch.id] || '') || 0;
        if (gross > 0) {
          channelGross += gross;
          channelNet += gross;
        }
      });

      // Also submit auto-populated fields that have values, adding channel totals
      // onto Gross/Net Revenue so the rollup reflects everything (POS + channels).
      for (const field of AUTO_FIELDS) {
        let val = autoValues[field.label];
        if (field.label === 'Gross Revenue') {
          const base = val ?? 0;
          val = base + channelGross;
        } else if (field.label === 'Net Revenue') {
          const base = val ?? 0;
          val = base + channelNet;
        }
        if (val != null && val !== 0) {
          manualEntries.push({
            section: field.section as 'REVENUE' | 'TAX' | 'OPERATIONS',
            label: field.label,
            manualValue: val,
            ...(field.isCount ? { itemCount: val } : {}),
          });
        }
      }

      // Build channel revenue list (used for DailyRevenueEntry submission AND
      // for backend-side IncomeEntry upsert via externalRef — single source of truth).
      const channelEntries: DailyRevenueEntry[] = channels
        .filter((ch) => channelValues[ch.id])
        .map((ch) => {
          const gross = parseFloat(channelValues[ch.id]) || 0;
          return {
            channelId: ch.id,
            grossAmount: gross,
            commissionAmount: 0,
            netAmount: gross,
          };
        });

      await eodApi.confirmDay({
        summaryDate,
        manualEntries,
        closingNotes: closingNotes || undefined,
        closingCash: closingCash ? parseFloat(closingCash) : undefined,
        tipsTotal: tipsTotal ? parseFloat(tipsTotal) : undefined,
        staffMealsCount: staffMealsCount ? parseInt(staffMealsCount, 10) : undefined,
        staffMealsValue: staffMealsValue ? parseFloat(staffMealsValue) : undefined,
        // Backend owns IncomeEntry creation now — upserts by externalRef so
        // re-confirm never duplicates rows. Frontend no longer calls createIncome().
        channelRevenues: channelEntries
          .filter((e) => e.grossAmount > 0)
          .map((e) => ({ channelId: e.channelId, grossAmount: e.grossAmount })),
      });

      if (channelEntries.length > 0) {
        await financeApi.submitDailyRevenue({
          date: summaryDate,
          entries: channelEntries,
        });
      }

      toast.success(`Day ${summaryDate} closed successfully`);
      navigate('/vendor/finance/eod-history');
    } catch (err: unknown) {
      const axiosErr = err as {
        response?: {
          data?: {
            message?: string;
            errorCode?: string;
            details?: Record<string, unknown>;
          };
        };
      };
      const data = axiosErr?.response?.data;
      if (data?.errorCode === 'ENTRY_SETTLED' && data.details && typeof data.details.settlementId === 'number') {
        const d = data.details as Record<string, unknown>;
        setSettlementBlock({
          settlementId: d.settlementId as number,
          allocationCount: typeof d.allocationCount === 'number' ? (d.allocationCount as number) : null,
          periodFrom: typeof d.periodFrom === 'string' ? (d.periodFrom as string) : null,
          periodTo: typeof d.periodTo === 'string' ? (d.periodTo as string) : null,
          channelId: typeof d.channelId === 'number' ? (d.channelId as number) : null,
          channelName: typeof d.channelName === 'string' ? (d.channelName as string) : null,
          entryDate: typeof d.entryDate === 'string' ? (d.entryDate as string) : null,
        });
      } else {
        const msg = data?.message || 'Failed to close day';
        toast.error(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  /* -- Shared styles ----------------------------------------------- */

  const inputStyle: React.CSSProperties = {
    width: '100%',
    boxSizing: 'border-box',
    padding: '12px 14px',
    fontSize: 16,
    fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
    background: isConfirmed
      ? (theme.components?.input?.disabled || theme.gray[50])
      : (theme.components?.input?.background || '#F0EDE8'),
    border: `1px solid ${theme.components?.input?.border || '#E8E6E1'}`,
    borderRadius: 10,
    color: theme.text,
    outline: 'none',
    minHeight: 44,
    transition: 'border-color 0.2s',
  };

  const labelStyle: React.CSSProperties = {
    fontSize: 13,
    fontWeight: 500,
    color: theme.gray[500],
    marginBottom: 6,
    display: 'block',
    fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
  };

  const sectionAccordionSx = {
    background: theme.components?.card?.background || '#FFFFFF',
    border: `1px solid ${theme.components?.card?.border || '#E8E6E1'}`,
    borderRadius: '16px !important',
    boxShadow: 'none',
    mb: 2,
    '&:before': { display: 'none' },
    '&.Mui-expanded': { margin: '0 0 16px 0' },
  };

  /* -- Render ------------------------------------------------------ */

  return (
    <Box>
      <FinanceTabBar />
      <PageHeroBand
        eyebrow={`Finance · ${summaryDate === todayStr.current ? 'Today' : summaryDate}${isConfirmed ? ' · Confirmed' : ''}`}
        title="End of Day Summary"
        actions={
          <>
            <input
              type="date"
              data-testid="eod-date-picker"
              value={summaryDate}
              max={todayStr.current}
              onChange={(e) => {
                const newDate = e.target.value;
                if (newDate && newDate !== summaryDate) {
                  navigate(`/vendor/finance/eod-summary?date=${newDate}`);
                }
              }}
              style={{
                border: '1px solid var(--border-default)',
                borderRadius: 'var(--r-md)',
                padding: '0 12px',
                fontSize: 14,
                fontFamily: 'inherit',
                backgroundColor: 'var(--bg-surface)',
                color: 'var(--text-primary)',
                height: 40,
                boxSizing: 'border-box',
                cursor: 'pointer',
              }}
            />
            {isHistorical && (
              <button
                data-testid="back-to-today"
                onClick={() => navigate('/vendor/finance/eod-summary')}
                style={{
                  background: 'var(--bg-surface)',
                  color: 'var(--action)',
                  border: '1px solid var(--border-default)',
                  borderRadius: 'var(--r-md)',
                  padding: '0 14px',
                  cursor: 'pointer',
                  fontSize: 14,
                  fontWeight: 600,
                  fontFamily: 'inherit',
                  height: 40,
                  boxSizing: 'border-box',
                }}
              >
                Today
              </button>
            )}
          </>
        }
      />
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <button
          onClick={() => navigate('/vendor/finance/eod-summary')}
          style={{
            padding: '0 16px',
            borderRadius: 20,
            border: '1px solid var(--action)',
            backgroundColor: 'var(--bg-selected)',
            color: 'var(--action)',
            fontWeight: 600,
            fontSize: '0.813rem',
            cursor: 'pointer',
            fontFamily: 'inherit',
            minHeight: 40,
          }}
        >
          Summary
        </button>
        <button
          onClick={() => navigate('/vendor/finance/eod-history')}
          style={{
            padding: '0 16px',
            borderRadius: 20,
            border: '1px solid var(--border-default)',
            backgroundColor: 'transparent',
            color: 'var(--text-secondary)',
            fontWeight: 600,
            fontSize: '0.813rem',
            cursor: 'pointer',
            fontFamily: 'inherit',
            minHeight: 40,
          }}
        >
          History
        </button>
      </Box>

      {/* Stale banner — confirmed snapshot is out of date because a source
          row (income/expense) for this date was edited after confirm. */}
      {isConfirmed && summary?.isStale && (
        <Box
          data-testid="stale-banner"
          sx={{
            background: 'var(--status-warn-bg)',
            color: 'var(--status-warn-text)',
            border: '1px solid var(--status-warn)',
            borderRadius: '12px',
            px: 2,
            py: 1.5,
            mb: 2,
            fontWeight: 600,
            fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <span style={{ flex: 1 }}>
            Out of date — {summary.staleReason
              ? `${summary.staleReason.toLowerCase().replace(/_/g, ' ')} after confirm`
              : 'source data changed after confirm'}.
            Re-confirm to refresh totals.
          </span>
          {!isFinanceOnlyRestricted && (
            <button
              data-testid="recompute-stale-eod"
              onClick={() => setSummary((prev) => prev ? { ...prev, status: 'DRAFT' } : prev)}
              style={{
                background: 'transparent',
                color: 'var(--status-warn-text)',
                border: '1px solid var(--status-warn-text)',
                borderRadius: 8,
                padding: '4px 12px',
                fontSize: '0.85rem',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Re-confirm
            </button>
          )}
        </Box>
      )}

      {/* Confirmed banner */}
      {isConfirmed && (
        <Box
          data-testid="confirmed-banner"
          sx={{
            background: theme.button?.success?.bg || '#DCFCE7',
            color: theme.button?.success?.text || '#16A34A',
            border: `1px solid ${theme.button?.success?.border || theme.success || '#16A34A'}`,
            borderRadius: '12px',
            px: 2,
            py: 1.5,
            mb: 2,
            fontWeight: 600,
            fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <span style={{ flex: 1 }}>Day Confirmed</span>
          {/* Restricted finance-only stewards get a read-only confirmation
              banner — no Edit/re-confirm. Vendor + regular vendor accounts
              keep the existing amend affordance. */}
          {!isFinanceOnlyRestricted && (
            <button
              data-testid="edit-confirmed-eod"
              onClick={() => {
                // Re-generate to get editable draft
                setSummary((prev) => prev ? { ...prev, status: 'DRAFT' } : prev);
              }}
              style={{
                background: 'transparent',
                color: theme.button?.success?.text || '#16A34A',
                border: `1px solid ${theme.button?.success?.text || '#16A34A'}`,
                borderRadius: 8,
                padding: '4px 12px',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              Edit
            </button>
          )}
        </Box>
      )}

      {/* Loading skeleton */}
      {loading && (
        <Box data-testid="loading-skeleton">
          <LoadingSkeleton variant="card" count={4} />
        </Box>
      )}

      {!loading && (
        <>
          {/* ==================== AUTO-POPULATED SECTION ==================== */}
          {/* Open by default. This section holds the day's actual figures —
              revenue, covers, tax, discounts — while the sections below it are
              manual boxes that are usually empty. Starting collapsed meant the
              page opened on a screen of zeros with the real numbers hidden, and
              a day with plenty of trade looked like a day with none. */}
          <Accordion data-testid="auto-populated-section" defaultExpanded sx={sectionAccordionSx}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: theme.gray[500] }} />}
              sx={{ px: 2.5, minHeight: 52 }}
            >
              <Box>
                <Typography
                  sx={{
                    fontFamily: "'Fraunces', Georgia, serif",
                    fontSize: 16,
                    fontWeight: 600,
                    color: theme.text,
                  }}
                >
                  POS Summary
                </Typography>
                <Typography sx={{ fontSize: 12, color: theme.gray[400], mt: 0.25 }}>
                  Auto-populated from {summaryDate === todayStr.current ? "today's" : summaryDate} orders
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 2.5, pt: 0, pb: 2 }}>
              {/* DISPLAY ONLY: read the entity rollup fields the backend actually
                  sends (the AUTO_FIELDS line-label lookup never matches a generate
                  payload, which is why this panel showed 0.00). autoValues stays
                  untouched — the confirm flow submits from it and its semantics
                  must not change here. Tax row label follows whichever tax line
                  the payload contains (VAT for GB, CGST for IN). */}
              {(() => {
                const taxOf = (label: string) => summary?.lines?.find(
                  (l) => l.section === 'TAX_BREAKDOWN' && l.label === label,
                );
                const amountOf = (label: string): number | null => {
                  const line = taxOf(label);
                  if (!line) return null;
                  return line.autoValue ?? line.finalValue ?? 0;
                };

                // Indian GST is charged as CGST + SGST, and inter-state as IGST.
                // Showing CGST alone reported HALF the tax collected — the bill
                // itemises both, and end of day has to agree with the bill. Rows
                // appear only when the payload carries them, so a GB vendor sees
                // VAT and nothing else.
                const vat = amountOf('VAT');
                const taxRows: { label: string; value: number }[] = [];
                if (vat != null) {
                  taxRows.push({ label: 'VAT Collected', value: vat });
                } else {
                  const cgst = amountOf('CGST');
                  const sgst = amountOf('SGST');
                  const igst = amountOf('IGST');
                  if (cgst != null) taxRows.push({ label: 'CGST Collected', value: cgst });
                  if (sgst != null) taxRows.push({ label: 'SGST Collected', value: sgst });
                  // Inter-state only; hidden when it does not apply.
                  if (igst) taxRows.push({ label: 'IGST Collected', value: igst });
                  if (taxRows.length === 0) {
                    taxRows.push({
                      label: 'CGST Collected',
                      value: autoValues['CGST Collected'] ?? 0,
                    });
                  }
                }

                const rows: { label: string; value: number; isCount?: boolean }[] = [
                  { label: 'Gross Revenue', value: summary?.grossRevenue ?? autoValues['Gross Revenue'] ?? 0 },
                  { label: 'Net Revenue', value: summary?.netRevenue ?? autoValues['Net Revenue'] ?? 0 },
                  ...taxRows,
                  { label: 'Total Tax', value: summary?.totalTax ?? 0 },
                  { label: 'Total Covers', value: summary?.covers ?? autoValues['Total Covers'] ?? 0, isCount: true },
                  { label: 'Total Discounts', value: summary?.totalDiscounts ?? autoValues['Total Discounts'] ?? 0 },
                ];
                return rows;
              })().map((field) => {
                const val = field.value;
                return (
                  <Box
                    key={field.label}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      py: 0.75,
                      borderBottom: `1px solid ${theme.gray[100] || '#F5F3EF'}`,
                      '&:last-child': { borderBottom: 'none' },
                    }}
                  >
                    <Typography sx={{ fontSize: 14, color: theme.gray[600] || theme.text }}>
                      {displayFieldLabel(field)}
                    </Typography>
                    <Typography sx={{ fontSize: 14, fontWeight: 600, color: theme.text }}>
                      {val != null
                        ? field.isCount
                          ? val
                          : formatCurrency(val)
                        : field.isCount
                          ? '0'
                          : formatCurrency(0)}
                    </Typography>
                  </Box>
                );
              })}
            </AccordionDetails>
          </Accordion>

          {/* ==================== REVENUE CHANNELS ACCORDION ==================== */}
          {channels.length > 0 && (
            <Accordion defaultExpanded sx={sectionAccordionSx}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: theme.gray[500] }} />}
                sx={{ px: 2.5, minHeight: 52 }}
              >
                <Typography
                  sx={{
                    fontFamily: "'Fraunces', Georgia, serif",
                    fontSize: 16,
                    fontWeight: 600,
                    color: theme.text,
                  }}
                >
                  Revenue Channels
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 2, pt: 0, pb: 2 }}>
                <RevenueBreakdownForm
                  channels={channels}
                  values={channelValues}
                  onChange={handleChannelChange}
                  disabled={isConfirmed}
                />
              </AccordionDetails>
            </Accordion>
          )}

          {/* ==================== MANUAL ENTRIES ACCORDION ==================== */}
          <Accordion defaultExpanded sx={sectionAccordionSx}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: theme.gray[500] }} />}
              sx={{ px: 2.5, minHeight: 52 }}
            >
              <Typography
                sx={{
                  fontFamily: "'Fraunces', Georgia, serif",
                  fontSize: 16,
                  fontWeight: 600,
                  color: theme.text,
                }}
              >
                Manual Entries
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 2.5, pt: 0, pb: 2 }}>
              {/* Tips Total */}
              <Box sx={{ mb: 1.5 }}>
                <label htmlFor="eod-tips" style={labelStyle}>Tips Total</label>
                <input
                  id="eod-tips"
                  type="text"
                  inputMode="decimal"
                  placeholder="0.00"
                  value={tipsTotal}
                  onChange={(e) => setTipsTotal(sanitizeDecimal(e.target.value))}
                  disabled={isConfirmed}
                  aria-label="Tips Total"
                  style={inputStyle}
                />
              </Box>

              {/* Staff Meals */}
              <Box sx={{ mb: 1.5 }}>
                <label style={labelStyle}>Staff Meals</label>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Box sx={{ flex: 1 }}>
                    <label htmlFor="eod-meals-count" style={{ ...labelStyle, fontSize: 12 }}>Count</label>
                    <input
                      id="eod-meals-count"
                      type="text"
                      inputMode="numeric"
                      placeholder="0"
                      value={staffMealsCount}
                      onChange={(e) => setStaffMealsCount(sanitizeInt(e.target.value))}
                      disabled={isConfirmed}
                      aria-label="Staff Meals Count"
                      style={inputStyle}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <label htmlFor="eod-meals-value" style={{ ...labelStyle, fontSize: 12 }}>Value</label>
                    <input
                      id="eod-meals-value"
                      type="text"
                      inputMode="decimal"
                      placeholder="0.00"
                      value={staffMealsValue}
                      onChange={(e) => setStaffMealsValue(sanitizeDecimal(e.target.value))}
                      disabled={isConfirmed}
                      aria-label="Staff Meals Value"
                      style={inputStyle}
                    />
                  </Box>
                </Box>
              </Box>

              {/* Closing Cash */}
              <Box sx={{ mb: 1.5 }}>
                <label htmlFor="eod-closing-cash" style={labelStyle}>Closing Cash</label>
                <input
                  id="eod-closing-cash"
                  type="text"
                  inputMode="decimal"
                  placeholder="0.00"
                  value={closingCash}
                  onChange={(e) => setClosingCash(sanitizeDecimal(e.target.value))}
                  disabled={isConfirmed}
                  aria-label="Closing Cash"
                  style={inputStyle}
                />
              </Box>

              {/* Closing Notes */}
              <Box>
                <label htmlFor="eod-notes" style={labelStyle}>Closing Notes</label>
                <textarea
                  id="eod-notes"
                  placeholder="Any notes for this day..."
                  value={closingNotes}
                  onChange={(e) => setClosingNotes(e.target.value)}
                  disabled={isConfirmed}
                  aria-label="Closing Notes"
                  rows={3}
                  style={{
                    ...inputStyle,
                    resize: 'vertical',
                    minHeight: 80,
                  }}
                />
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* The legacy global "Record revenue as Income" checkbox was
              removed — income recording is now governed per-channel via
              `RevenueChannel.recordRevenueAsIncome` (Finance Settings →
              Channels). Aggregator channels skip the EOD income write and
              instead create an IncomeEntry when a settlement is saved. */}

          {/* ==================== CONFIRM BUTTON ==================== */}
          {!isConfirmed && !showDialog && (
            <div>
              <button
                data-testid="confirm-button"
                onClick={handleSubmit}
                disabled={!canSubmit || submitting}
                style={{
                  width: '100%',
                  minHeight: 48,
                  background: canSubmit
                    ? (theme.button?.primary?.bg || theme.primary)
                    : (theme.button?.primary?.disabled || theme.gray[200]),
                  color: canSubmit
                    ? (theme.button?.primary?.text || '#FFFFFF')
                    : theme.gray[500],
                  border: 'none',
                  borderRadius: 12,
                  fontSize: 16,
                  fontWeight: 600,
                  cursor: canSubmit ? 'pointer' : 'default',
                  fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
                  opacity: submitting ? 0.7 : 1,
                  textAlign: 'center',
                  padding: '12px 16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {submitting ? 'Submitting...' : isHistorical ? `Confirm ${summaryDate}` : 'Confirm & Close Day'}
              </button>
              {!canSubmit && !submitting && (
                <p
                  style={{
                    fontSize: 13,
                    color: theme.gray[500],
                    textAlign: 'center',
                    marginTop: 8,
                    fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
                  }}
                >
                  {hasNegativeChannels
                    ? 'Revenue channel amounts cannot be negative'
                    : 'No POS revenue for this day — enter a revenue channel or manual entry to close it'}
                </p>
              )}
            </div>
          )}

          {/* ==================== CONFIRMATION DIALOG ==================== */}
          <Dialog
            open={showDialog}
            onClose={() => setShowDialog(false)}
            data-testid="confirm-dialog"
            maxWidth="sm"
            fullWidth
            PaperProps={{
              sx: {
                background: theme.components?.modal?.background || '#FFFFFF',
                borderRadius: '16px',
                maxWidth: 400,
                boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
              },
            }}
          >
            <DialogTitle
              sx={{
                fontFamily: "'Fraunces', Georgia, serif",
                fontSize: 20,
                fontWeight: 700,
                color: theme.text,
                pb: 1,
              }}
            >
              Close the Day?
            </DialogTitle>
            <DialogContent>
              {/* POS Summary in dialog */}
              {AUTO_FIELDS.some((f) => autoValues[f.label] != null) && (
                <Box sx={{ mb: 2 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: theme.gray[500], mb: 1 }}>
                    POS Summary
                  </Typography>
                  {AUTO_FIELDS.filter((f) => autoValues[f.label] != null).map((f) => (
                    <Box
                      key={f.label}
                      sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, fontSize: 14, color: theme.text }}
                    >
                      <span>{displayFieldLabel(f)}</span>
                      <span style={{ fontWeight: 600 }}>
                        {f.isCount ? autoValues[f.label] : formatCurrency(autoValues[f.label]!)}
                      </span>
                    </Box>
                  ))}
                </Box>
              )}

              {/* Channel entries in dialog */}
              {channels.some((ch) => channelValues[ch.id]) && (
                <Box
                  data-testid="dialog-channel-entries"
                  sx={{
                    mb: 2,
                    borderTop: `1px solid ${theme.gray[200]}`,
                    pt: 1.5,
                  }}
                >
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: theme.gray[500], mb: 1 }}>
                    Revenue Channels
                  </Typography>
                  {channels
                    .filter((ch) => channelValues[ch.id])
                    .map((ch) => (
                      <Box
                        key={ch.id}
                        sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, fontSize: 14, color: theme.text }}
                      >
                        <span>{ch.name}</span>
                        <span style={{ fontWeight: 600 }}>
                          {formatCurrency(parseFloat(channelValues[ch.id]) || 0)}
                        </span>
                      </Box>
                    ))}
                </Box>
              )}

              {/* Manual entries in dialog */}
              {hasManualValues && (
                <Box sx={{ mb: 2, borderTop: `1px solid ${theme.gray[200]}`, pt: 1.5 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: theme.gray[500], mb: 1 }}>
                    Manual Entries
                  </Typography>
                  {tipsTotal && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, fontSize: 14, color: theme.text }}>
                      <span>Tips Total</span>
                      <span style={{ fontWeight: 600 }}>{formatCurrency(parseFloat(tipsTotal))}</span>
                    </Box>
                  )}
                  {staffMealsCount && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, fontSize: 14, color: theme.text }}>
                      <span>Staff Meals</span>
                      <span style={{ fontWeight: 600 }}>
                        {staffMealsCount} meals
                        {staffMealsValue ? ` (${formatCurrency(parseFloat(staffMealsValue))})` : ''}
                      </span>
                    </Box>
                  )}
                  {closingCash && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, fontSize: 14, color: theme.text }}>
                      <span>Closing Cash</span>
                      <span style={{ fontWeight: 600 }}>{formatCurrency(parseFloat(closingCash))}</span>
                    </Box>
                  )}
                </Box>
              )}

              {closingNotes && (
                <Box sx={{ mb: 1, borderTop: `1px solid ${theme.gray[200]}`, pt: 1.5 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: theme.gray[500], mb: 0.5 }}>
                    Notes
                  </Typography>
                  <Typography sx={{ fontSize: 14, color: theme.text }}>{closingNotes}</Typography>
                </Box>
              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, gap: 1, flexWrap: 'wrap' }}>
              <button
                onClick={() => setShowDialog(false)}
                style={{
                  background: theme.button?.secondary?.bg || '#FFFFFF',
                  color: theme.button?.secondary?.text || theme.primary,
                  border: `1px solid ${theme.button?.secondary?.border || theme.primary}`,
                  borderRadius: 10,
                  padding: '10px 20px',
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer',
                  minHeight: 44,
                  minWidth: 96,
                  whiteSpace: 'nowrap',
                }}
              >
                Cancel
              </button>
              <button
                data-testid="dialog-confirm-button"
                onClick={handleConfirmClose}
                style={{
                  background: theme.button?.primary?.bg || theme.primary,
                  color: theme.button?.primary?.text || '#FFFFFF',
                  border: 'none',
                  borderRadius: 10,
                  padding: '10px 20px',
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer',
                  minHeight: 44,
                  whiteSpace: 'nowrap',
                }}
              >
                Confirm & Close Day
              </button>
            </DialogActions>
          </Dialog>
        </>
      )}

      <SettlementRecoveryDialog
        open={settlementBlock !== null}
        details={settlementBlock}
        onClose={() => setSettlementBlock(null)}
        onResolved={() => {
          setSettlementBlock(null);
          // Re-run the close-day flow now that the blocking settlement is gone.
          handleConfirmClose();
        }}
      />
    </Box>
  );
}
